import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { ampli } from '@/ampli';
import { useSpring } from '@react-spring/web';
import {
  KeyboardEventHandler,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { logger } from '@/lib/logger';

import { THEME_STORAGE_KEY } from '@/constants/preferences';

import { fontStyles } from '@/styles/typography';

import { CheckmarkIcon } from '@/ui/Icons/CheckmarkIcon';
import { ChevronLeftIcon } from '@/ui/Icons/ChevronLeftIcon';
import { LaptopIcon } from '@/ui/Icons/LaptopIcon';
import { NightThemeIcon } from '@/ui/Icons/NightThemeIcon';
import { SunIcon } from '@/ui/Icons/SunIcon';

import { IDENTIFY_TYPES, identifyUserProperties } from '@/utils/amplitude';
import { AnimatedDiv } from '@/utils/animated-div';
import { designTokenToRawMapping } from '@/utils/colors';

export type ThemeMode = 'light' | 'dark' | 'system';

const THEMES = [
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'dark', label: 'Dark', icon: NightThemeIcon },
  { value: 'system', label: 'System', icon: LaptopIcon },
];

function setToggleMode(mode: ThemeMode) {
  localStorage.setItem(THEME_STORAGE_KEY, mode);
  document.documentElement.dataset['theme'] = mode;
  document.body.dataset['theme'] = mode;
}

interface DarkmodeTogglePageProps {
  onGoBack: (e: React.MouseEvent | React.KeyboardEvent) => void;
}

export const DarkmodeContext = createContext<{
  theme: ThemeMode;
  setTheme: (t: ThemeMode) => void;
}>({
  theme: 'system',
  setTheme: () => {},
});

const rawHomepageBg = designTokenToRawMapping('color-special-background-homepage');

function updateMetaThemeColor(mode: Exclude<ThemeMode, 'system'>) {
  if (!rawHomepageBg) {
    logger.warn('WARNING: missing homepage color mapping for theme color');
    return;
  }
  let metaTag = document.querySelector('meta[name="theme-color"]');
  if (!metaTag) {
    metaTag = document.createElement('meta');
    metaTag.setAttribute('name', 'theme-color');
    document.head.appendChild(metaTag);
  }
  metaTag.setAttribute('content', mode === 'dark' ? rawHomepageBg.dark : rawHomepageBg.light);
}

export function DarkmodeProvider({ children }: { children: (theme: ThemeMode) => ReactNode }) {
  const [theme, setTheme] = useState<ThemeMode>('system');

  useEffect(() => {
    // listen for changes across tabs
    const listener = (event: StorageEvent) => {
      if (event.key === THEME_STORAGE_KEY) {
        setToggleMode(event.newValue as ThemeMode);
        setTheme(event.newValue as ThemeMode);
      }
    };

    window.addEventListener('storage', listener);

    // initialize
    const previousValue = localStorage.getItem(THEME_STORAGE_KEY);
    if (previousValue) {
      setTheme(previousValue as ThemeMode);
    }
  }, []);

  // analytics
  useEffect(() => {
    let listener: null | ((e: MediaQueryListEvent) => void) = null;
    const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)');
    if (theme === 'system') {
      listener = (e: MediaQueryListEvent) => {
        if (e.matches) {
          // we are in system mode and switched into dark mode
          identifyUserProperties({
            type: IDENTIFY_TYPES.SET,
            key: 'theme_toggle_position',
            value: 'dark',
          });
          updateMetaThemeColor('dark');
        } else {
          // we are in system mode and switched into light mode
          identifyUserProperties({
            type: IDENTIFY_TYPES.SET,
            key: 'theme_toggle_position',
            value: 'light',
          });
          updateMetaThemeColor('light');
        }
      };
      isDarkMode.addEventListener('change', listener);
      if (isDarkMode.matches) {
        identifyUserProperties({
          type: IDENTIFY_TYPES.SET,
          key: 'theme_toggle_position',
          value: 'dark',
        });
        updateMetaThemeColor('dark');
      } else {
        identifyUserProperties({
          type: IDENTIFY_TYPES.SET,
          key: 'theme_toggle_position',
          value: 'light',
        });
        updateMetaThemeColor('light');
      }
    } else {
      // user explicitly set light or dark mode
      identifyUserProperties({
        type: IDENTIFY_TYPES.SET,
        key: 'theme_toggle_position',
        value: theme,
      });
      updateMetaThemeColor(theme);
    }

    return () => {
      if (listener !== null) {
        isDarkMode.removeEventListener('change', listener);
      }
    };
  }, [theme]);

  return (
    <DarkmodeContext.Provider value={{ theme, setTheme }}>
      {children(theme)}
    </DarkmodeContext.Provider>
  );
}

export function DarkmodeTogglePage({ onGoBack }: DarkmodeTogglePageProps) {
  const { theme, setTheme } = useContext(DarkmodeContext);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const toggleGroupRootRef = useRef<HTMLDivElement>(null);

  const props = useSpring({
    from: {
      transform: 'translateX(80px)',
    },
    to: {
      transform: 'translateX(0)',
    },
  });

  useEffect(() => {
    // on mount, set focus to the back button
    buttonRef.current?.focus();

    // containerRef.current?.focus();
  }, []);

  const onChangeHandler = (newVal: ThemeMode) => {
    if (!newVal) return;
    setToggleMode(newVal);
    setTheme(newVal);
    ampli.themeToggled({ theme: newVal });
  };

  const buttonKeyDownHandler: KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (e.key == 'ArrowDown') {
      toggleGroupRootRef.current?.focus();
    }
  };

  const groupKeyDownHandler: KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (
      e.key === 'ArrowUp' &&
      (e.currentTarget.firstChild as HTMLButtonElement).getAttribute('tabindex') === '0'
    ) {
      buttonRef.current?.focus();
    }
  };

  return (
    <div>
      <div
        className={fontStyles({
          variant: 'BODY_MD',
          class: [
            'text-token-gray-1600',
            'font-semibold',
            'p-1',
            'flex',
            'mx-0.5',
            'mt-0.5',
            'items-center',
            'gap-1',
            'w-full',
            'rounded-md',
          ],
        })}
      >
        <button
          ref={buttonRef}
          className="text-token-gray-1600 rounded-sm w-7 h-7 flex items-center justify-center hover:bg-token-gray-200 focus-visible:bg-token-gray-200 focus-visible:outline-none"
          onKeyDown={buttonKeyDownHandler}
          aria-label="Go back"
          onClick={onGoBack}
        >
          <ChevronLeftIcon />
        </button>
        Appearance
      </div>
      <AnimatedDiv style={props}>
        <ToggleGroup.Root
          ref={toggleGroupRootRef}
          className="flex flex-col p-0.5 text-token-gray-1600"
          type="single"
          loop={false}
          value={theme}
          onKeyDown={groupKeyDownHandler}
          onValueChange={onChangeHandler}
        >
          {THEMES.map(({ value, label, icon: ThemeIcon }) => (
            <ToggleGroup.Item
              key={value}
              value={value}
              className={fontStyles({
                variant: 'BODY_MD',
                class:
                  'pl-2.5 pr-4 py-2 flex flex-row items-center gap-3 rounded-md relative text-current hover:bg-token-gray-200',
              })}
            >
              <ThemeIcon className="text-token-gray-1600" />
              {label}
              {theme === value && (
                <CheckmarkIcon className="absolute top-0 bottom-0 my-auto right-3 text-token-gray-800" />
              )}
            </ToggleGroup.Item>
          ))}
        </ToggleGroup.Root>
      </AnimatedDiv>
    </div>
  );
}
